import React from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { showToast } from "src/app/methods/showToast";
import { setShowcaseAudienceOverlap } from "src/redux";
import { RootState } from "src/redux/reducers";
import {
  calculateCreatorsAudienceOverlap,
  getCreatorsAudienceOverlapState,
} from "src/app/components/AudienceOverlap/creatorsAudienceOverlapApi";
import { WorkspacePermissionsEnum } from "src/utils/PermissionsEnums";
import { getShowcaseSettingByKey } from "../../utils";
import {
  buildAudienceOverlapPages,
  getAudienceOverlapSocialProfiles,
  hasEnoughSocialProfilesForAudienceOverlap,
} from "./audienceOverlapUtils";

type FailureReason = "failed" | "not_enough_creators" | "timeout" | "error";

const getFailureMessage = (reason: FailureReason) => {
  switch (reason) {
    case "not_enough_creators":
      return (
        <IDHFormattedMessage
          id="ws_audience_overlap_not_enough_creators"
          defaultMessage="Audience overlap needs at least two creators with an Instagram, TikTok or YouTube account on the same platform."
        />
      );
    case "failed":
      return (
        <IDHFormattedMessage
          id="ws_audience_overlap_not_enough_accounts_with_data"
          defaultMessage="Audience overlap needs at least two creators with audience data on the same platform."
        />
      );
    case "timeout":
      return (
        <IDHFormattedMessage
          id="ws_audience_overlap_takes_too_long"
          defaultMessage="The analysis is taking longer than expected. Please try again in a moment."
        />
      );
    default:
      return (
        <IDHFormattedMessage
          id="ws_audience_overlap_could_not_be_calculated"
          defaultMessage="We could not calculate audience overlap for the selected creators."
        />
      );
  }
};

export function useShowcaseAudienceOverlap() {
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  const status = useSelector(
    (state: RootState) => state.showcaseReducer.audienceOverlap.status,
  );
  const isAudienceOverlapEnabled = useSelector(
    (state: RootState) => state.mainReducer.identity?.isAudienceOverlapEnabled,
  );

  const workspacePermissions = useSelector(
    (state: RootState) => state.mainReducer.identity?.permissions?.workspace,
  );
  const contextData = useSelector(
    (state: RootState) => state.showcaseReducer.showcaseData?.contextData,
  );

  // A client of a campaign has neither of these, so the audience overlap is not available to them
  const canCalculate =
    workspacePermissions?.includes(WorkspacePermissionsEnum.CREATOR_DATABASE) ||
    workspacePermissions?.includes(
      WorkspacePermissionsEnum.CREATOR_DISCOVERY,
    ) ||
    Boolean(contextData?.canCalculateAudienceOverlap);

  const available = Boolean(isAudienceOverlapEnabled && canCalculate);

  const getElements = () =>
    store.getState().showcaseReducer.showcaseData?.elements || [];

  const getSocialProfiles = () =>
    getAudienceOverlapSocialProfiles(getElements());

  // there is nothing to compare a single account on a platform with
  const hasEnoughCreators = () =>
    hasEnoughSocialProfilesForAudienceOverlap(getSocialProfiles());

  // Responses of requests sent for a previous set of creators (or before turning the overlap off) are ignored
  const getRequestVersion = (): number =>
    store.getState().showcaseReducer.audienceOverlap.requestVersion;

  const startNewRequestVersion = () => {
    const requestVersion = getRequestVersion() + 1;

    dispatch(setShowcaseAudienceOverlap({ requestVersion }));

    return requestVersion;
  };

  const sendRequest = (
    send: typeof calculateCreatorsAudienceOverlap,
    force = false,
  ) =>
    send({
      socialProfiles: getSocialProfiles(),
      wsProjectUuid: contextData?.wsProjectUuid,
      workspaceUuid: getShowcaseSettingByKey("workspaceId"),
      force,
    });

  const reset = (retryWithForce = false) => {
    dispatch(
      setShowcaseAudienceOverlap({
        status: "off",
        pages: [],
        boxes: {},
        retryWithForce,
      }),
    );
  };

  const fail = (reason: FailureReason) => {
    reset(true);
    showToast(
      "error",
      <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
      getFailureMessage(reason),
    );
  };

  // Returns true when the result has been applied to the showcase
  const applyDoneResult = (content: any) => {
    const { pages, boxes } = buildAudienceOverlapPages(
      content.audienceOverlapData,
      getElements(),
    );

    if (!pages.length) return false;

    dispatch(
      setShowcaseAudienceOverlap({
        status: "done",
        pages,
        boxes,
        retryWithForce: false,
      }),
    );

    return true;
  };

  const applyResult = (content: any, version: number) => {
    if (version !== getRequestVersion()) return;

    // "not_calculated" in the middle of an analysis is not a failure yet. Polling goes on and gives up
    // only when the API keeps reporting it, which means the pending analysis has expired.
    if (["pending", "not_calculated"].includes(content?.status)) return;

    if (content?.status === "done" && applyDoneResult(content)) {
      showToast(
        "success",
        <IDHFormattedMessage
          id="ws_analysis_complete"
          defaultMessage="Analysis complete"
        />,
        <IDHFormattedMessage
          id="ws_audience_overlap_has_been_added_to_your_showcase"
          defaultMessage="Audience overlap has been added to your showcase."
        />,
      );

      return;
    }

    const hasAnyPlatform = Object.keys(
      content?.audienceOverlapData || {},
    ).length;

    fail(hasAnyPlatform ? "failed" : "not_enough_creators");
  };

  const run = async () => {
    if (!available) return;

    if (!hasEnoughCreators()) {
      fail("not_enough_creators");

      return;
    }

    const version = startNewRequestVersion();
    const { retryWithForce } = store.getState().showcaseReducer.audienceOverlap;

    dispatch(
      setShowcaseAudienceOverlap({ status: "pending", pages: [], boxes: {} }),
    );

    document
      .querySelector(".cover-audience-overlap")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });

    try {
      applyResult(
        await sendRequest(calculateCreatorsAudienceOverlap, retryWithForce),
        version,
      );
    } catch (error) {
      if (version === getRequestVersion()) fail("error");
    }
  };

  // Called while the analysis is pending. A failed request is not an error yet, the next attempt may succeed.
  // Returns the status reported by the API, so the caller knows whether the analysis is still going on.
  const poll = async (): Promise<string | undefined> => {
    if (!hasEnoughCreators()) return undefined;

    const version = getRequestVersion();

    try {
      const content = await sendRequest(getCreatorsAudienceOverlapState);

      applyResult(content, version);

      return version === getRequestVersion() ? content?.status : undefined;
    } catch (error) {
      console.error(error);

      return undefined;
    }
  };

  const timeout = () => {
    startNewRequestVersion();
    fail("timeout");
  };

  const turnOff = () => {
    startNewRequestVersion();
    reset();
  };

  // The set of creators has changed: the previous result is not valid anymore.
  // If the new set has already been analyzed it is shown right away, otherwise the analysis has to be run again.
  const restore = async () => {
    const version = startNewRequestVersion();

    reset();

    let restored = false;

    if (available && hasEnoughCreators()) {
      try {
        const content = await sendRequest(getCreatorsAudienceOverlapState);

        if (version !== getRequestVersion()) return;

        restored = content?.status === "done" && applyDoneResult(content);
      } catch (error) {
        console.error(error);
      }
    }

    if (restored || version !== getRequestVersion()) return;

    showToast(
      "info",
      <IDHFormattedMessage
        id="ws_audience_overlap_turned_off"
        defaultMessage="Audience overlap turned off"
      />,
      <IDHFormattedMessage
        id="ws_audience_overlap_turned_off_after_creators_change"
        defaultMessage="The list of creators has changed, so the previous analysis no longer applies. Run the analysis again to add it to your showcase."
      />,
    );
  };

  return { available, status, run, poll, timeout, turnOff, restore };
}
